import { createReducer } from '@reduxjs/toolkit';

import { Loadable, CommentManager } from 'models';
import { makeThunkFromAPICall } from 'state';

export type FeedbackState = {
    comments: Loadable<CommentManager.Comment[]>;
    replies: Record<number, Loadable<CommentManager.Comment[]>>;
    addCommentResults: Loadable<CommentManager.API.ActionResponse>;
    updateCommentResults: Loadable<CommentManager.API.ActionResponse>;
    deleteCommentResults: Loadable<CommentManager.API.ActionResponse>;
    likeCommentResults: Loadable<CommentManager.API.ActionResponse>;
    unlikeCommentResults: Loadable<CommentManager.API.ActionResponse>;
};

const initialState: FeedbackState = {
    comments: {
        status: 'idle',
    },
    replies: {},
    addCommentResults: {
        status: 'idle',
    },
    updateCommentResults: {
        status: 'idle',
    },
    deleteCommentResults: {
        status: 'idle',
    },
    likeCommentResults: {
        status: 'idle',
    },
    unlikeCommentResults: {
        status: 'idle',
    },
};

export const [getComments, setComments] = makeThunkFromAPICall(
    CommentManager.getComments,
    'comments/get-top',
);

export const [getReplies, setReplies] = makeThunkFromAPICall(
    CommentManager.getReplies,
    'comments/get-replies',
);

export const [addComment, setAddComment] = makeThunkFromAPICall(
    CommentManager.addComment,
    'comments/add',
);

export const [likeComment, setLikeComment] = makeThunkFromAPICall(
    CommentManager.likeComment,
    'comments/like',
);

export const [unlikeComment, setUnlikeComment] = makeThunkFromAPICall(
    CommentManager.unlikeComment,
    'comments/unlike',
);

export const [updateComment, setUpdateComment] = makeThunkFromAPICall(
    CommentManager.updateComment,
    'comments/update',
);

export const [deleteComment, setDeleteComment] = makeThunkFromAPICall(
    CommentManager.deleteComment,
    'comments/delete',
);

export const feedbackReducer = createReducer(initialState, (builder) => {
    builder.addCase(setComments, (state, action) => {
        return {
            ...state,
            comments: { ...state.comments, ...action.payload.loadable },
        };
    });

    builder.addCase(setReplies, (state, action) => {
        return {
            ...state,
            replies: {
                ...state.replies,
                [action.payload.input.parent]: {
                    ...state.replies[action.payload.input.parent],
                    ...action.payload.loadable,
                },
            },
        };
    });

    builder.addCase(setAddComment, (state, action) => {
        return {
            ...state,
            addCommentResults: {
                ...state.addCommentResults,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setUpdateComment, (state, action) => {
        return {
            ...state,
            updateCommentResults: {
                ...state.updateCommentResults,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setDeleteComment, (state, action) => {
        return {
            ...state,
            likeCommentResults: {
                ...state.likeCommentResults,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setLikeComment, (state, action) => {
        return {
            ...state,
            deleteCommentResults: {
                ...state.deleteCommentResults,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setUnlikeComment, (state, action) => {
        return {
            ...state,
            unlikeCommentResults: {
                ...state.unlikeCommentResults,
                ...action.payload.loadable,
            },
        };
    });
});

export const FeedbackActions = {
    getComments,
    getReplies,
    addComment,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment,
};

export default feedbackReducer;
